<template>
  <div class="view-container">
    <div class="main-layout">
      <div class="left-panel">
        <div class="panel-header"><h4>模块列表</h4></div>
        <div class="panel-body">
          <table class="tree-table">
            <thead><tr><th>模块名称</th></tr></thead>
            <tbody>
              <template v-for="node in moduleTree" :key="node.guid">
                <tr class="tree-row" :class="{ selected: selectedModuleGuid === node.guid }" @click="selectModule(node)">
                  <td>
                    <span class="tree-name-cell" :style="{ paddingLeft: node._depth * 16 + 'px' }">
                      <span v-if="node.childModules && node.childModules.length > 0" class="expand-icon" :class="{ expanded: expandedModuleGuids.includes(node.guid) }" @click.stop="toggleExpandModule(node.guid)"><i class="fas fa-caret-right"></i></span>
                      <span v-else class="expand-placeholder"></span>
                      <i class="fas" :class="node.childModules && node.childModules.length > 0 ? 'fa-folder' : 'fa-cube'"></i>
                      <span class="tree-name">{{ node.name }}</span>
                    </span>
                  </td>
                </tr>
                <template v-if="node.childModules && node.childModules.length > 0 && expandedModuleGuids.includes(node.guid)">
                  <tr v-for="child in flattenModuleChildren(node.childModules, node._depth + 1)" :key="child.guid" class="tree-row" :class="{ selected: selectedModuleGuid === child.guid }" @click="selectModule(child)">
                    <td>
                      <span class="tree-name-cell" :style="{ paddingLeft: child._depth * 16 + 'px' }">
                        <span v-if="child.childModules && child.childModules.length > 0" class="expand-icon" :class="{ expanded: expandedModuleGuids.includes(child.guid) }" @click.stop="toggleExpandModule(child.guid)"><i class="fas fa-caret-right"></i></span>
                        <span v-else class="expand-placeholder"></span>
                        <i class="fas" :class="child.childModules && child.childModules.length > 0 ? 'fa-folder' : 'fa-cube'"></i>
                        <span class="tree-name">{{ child.name }}</span>
                      </span>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div class="right-panel">
        <div class="panel-body">
          <div v-if="selectedModuleGuid">
            <div class="search-panel">
              <div class="search-row">
                <div class="search-item">
                  <label><i class="fas fa-cube"></i> 模块名称</label>
                  <input v-model="searchForm.moduleName" type="text" placeholder="搜索模块名称" class="search-input">
                </div>
                <div class="search-item">
                  <label><i class="fas fa-tag"></i> 授权类型</label>
                  <select v-model="searchForm.targetType" class="search-select">
                    <option value="">全部</option>
                    <option value="user">用户</option>
                    <option value="org">组织</option>
                    <option value="role">角色</option>
                  </select>
                </div>
                <div class="search-actions">
                  <button @click="handleSearch" class="btn btn-primary search-btn"><i class="fas fa-search"></i> 查询</button>
                  <button @click="resetSearch" class="btn btn-default search-btn"><i class="fas fa-undo"></i> 重置</button>
                  <button @click="openAddDialog" class="btn btn-success search-btn"><i class="fas fa-plus"></i> 新增权限</button>
                </div>
              </div>
            </div>

            <div class="table-container-inner">
              <table class="data-table">
                <thead><tr>
                  <th>模块名称</th><th width="80">授权类型</th><th>授权对象</th>
                  <th width="80">权限范围</th><th width="70">状态</th><th width="120">操作</th>
                </tr></thead>
                <tbody>
                  <tr v-for="p in paginatedData" :key="p.id">
                    <td>{{ p.moduleName }}</td>
                    <td><span class="type-badge" :class="p.targetType">{{ typeLabels[p.targetType] }}</span></td>
                    <td>{{ p.targetName }}</td>
                    <td>{{ p.scope === 'manage' ? '可管理' : '可查看' }}</td>
                    <td><span class="status-badge" :class="p.status==='启用'?'enabled':'disabled'">{{ p.status }}</span></td>
                    <td class="action-cell">
                      <button @click="editPermission(p)" class="btn btn-primary btn-xs" title="编辑"><i class="fas fa-edit"></i></button>
                      <button @click="toggleStatus(p)" class="btn btn-xs" :class="p.status==='启用'?'btn-warning':'btn-success'"><i class="fas" :class="p.status==='启用'?'fa-ban':'fa-check'"></i></button>
                      <button @click="deletePermission(p)" class="btn btn-danger btn-xs"><i class="fas fa-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="paginatedData.length === 0"><td colspan="6" class="empty-cell">暂无权限数据</td></tr>
                </tbody>
              </table>
            </div>

            <div class="pagination-row">
              <div class="pagination-info">共 {{ totalCount }} 条记录</div>
              <div class="pagination-controls">
                <div class="page-size-selector"><span>每页</span><select v-model="pageSize" class="page-size-input form-control"><option :value="10">10</option><option :value="20">20</option><option :value="50">50</option></select><span>条</span></div>
                <button @click="prevPage" :disabled="currentPage===1" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-left"></i></button>
                <div class="page-numbers"><button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="['page-btn',{active:currentPage===page,disabled:page==='...'}]" :disabled="page==='...'">{{ page }}</button></div>
                <button @click="nextPage" :disabled="currentPage>=totalPages" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-right"></i></button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state"><i class="fas fa-cubes"></i><p>请选择一个模块</p><p class="hint">左侧模块树点击即可查看对应模块权限</p></div>
        </div>
      </div>
    </div>

    <div v-if="showFormModal" class="modal-overlay" @click.self="closeFormModal">
      <div class="modal-container">
        <div class="modal-header"><h3>{{ editingId ? '编辑权限' : '新增权限' }}</h3><button @click="closeFormModal" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="form-row"><div class="form-group"><label>模块 *</label><div class="search-select"><input v-model="form.moduleName" type="text" class="form-control" readonly @click="showModulePicker = true"><button @click="showModulePicker = true" class="search-btn"><i class="fas fa-search"></i></button></div></div></div>
          <div class="form-row">
            <div class="form-group"><label>授权类型 *</label><select v-model="form.targetType" class="form-control"><option value="user">用户</option><option value="org">组织</option><option value="role">角色</option></select></div>
            <div class="form-group"><label>授权对象 *</label><div class="search-select"><input v-model="form.targetName" type="text" class="form-control" readonly @click="openTargetPicker"><button @click="openTargetPicker" class="search-btn"><i class="fas fa-search"></i></button></div></div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>权限范围</label><select v-model="form.scope" class="form-control"><option value="view">可查看</option><option value="manage">可管理</option></select></div>
            <div class="form-group"><label>状态</label><select v-model="form.status" class="form-control"><option value="启用">启用</option><option value="禁用">禁用</option></select></div>
          </div>
          <div class="form-group"><label>备注</label><textarea v-model="form.remark" class="form-control" rows="2"></textarea></div>
        </div>
        <div class="modal-footer"><button @click="closeFormModal" class="btn btn-default">取消</button><button @click="saveForm" class="btn btn-primary">保存</button></div>
      </div>
    </div>

    <div v-if="showModulePicker" class="modal-overlay" @click.self="showModulePicker=false">
      <div class="modal-container picker-modal">
        <div class="modal-header"><h3>选择模块</h3><button @click="showModulePicker=false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="search-box"><input v-model="moduleSearch" class="form-control" placeholder="搜索模块"></div>
          <div class="picker-list">
            <div v-for="m in filteredModules" :key="m.guid" class="picker-item" @click="selectModuleInPicker(m)" :class="{ selected: form.moduleId === m.guid }">
              <i class="fas" :class="m.icon || 'fa-cube'"></i><span>{{ m.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showTargetPicker" class="modal-overlay" @click.self="showTargetPicker=false">
      <div class="modal-container picker-modal">
        <div class="modal-header"><h3>选择{{ targetTypeLabel }}</h3><button @click="showTargetPicker=false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="search-box">
            <div class="search-input-wrap">
              <i class="fas fa-search search-icon"></i>
              <input v-model="targetSearch" class="search-box-input" :placeholder="'搜索' + targetTypeLabel">
            </div>
          </div>
          <div class="picker-list">
            <template v-if="form.targetType === 'user'">
              <div v-for="item in filteredTargets" :key="item.id" class="picker-item" @click="selectTarget(item)" :class="{ selected: form.targetId === item.id }">
                <i class="fas fa-user"></i>
                <span>{{ item.name }}</span>
              </div>
            </template>
            <template v-else>
              <div v-for="item in filteredTargets" :key="item.id" class="picker-item tree-item" :class="['tree-level-' + (item.level || 0), { selected: form.targetId === item.id }]" @click="selectTarget(item)">
                <i class="fas" :class="form.targetType==='org'?'fa-building':'fa-user-tag'"></i>
                <span>{{ item.name }}</span>
                <span v-if="item.hasChildren" class="tree-hint">···</span>
              </div>
            </template>
            <div v-if="filteredTargets.length === 0" class="picker-empty">无匹配结果</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { getModulePermissions, saveModulePermission, deleteModulePermission } from '../api/permission'
import { getModules } from '../api/system'
import { getOrgTree, getOrgList } from '../api/org'
import { getRoleList } from '../api/role'
import { getUserList } from '../api/user'

const typeLabels = { user: '用户', org: '组织', role: '角色' }
const targetTypeToOperator = { user: 'User', org: 'Org', role: 'Role' }
const currentPage = ref(1); const pageSize = ref(10)
const selectedModuleGuid = ref(null); const expandedModuleGuids = ref([])
const showFormModal = ref(false); const showModulePicker = ref(false); const showTargetPicker = ref(false)
const editingId = ref(null); const moduleSearch = ref(''); const targetSearch = ref('')

const searchForm = reactive({ moduleName: '', targetType: '' })
const form = reactive({ moduleId: null, moduleName: '', targetType: 'user', targetId: null, targetName: '', scope: 'view', status: '启用', remark: '' })

const moduleTree = ref([])
const flatModules = ref([])
const permissions = ref([])
const users = ref([])
const orgs = ref([])
const rls = ref([])
const orgTree = ref([])   // 树形数据
const roleTree = ref([])  // 树形数据

/* ---------- 辅助函数 ---------- */

/** 扁平化树形数据为列表 */
function flattenTree(nodes) {
  if (!nodes) return []
  const result = []
  const walk = (items) => {
    for (const n of items) {
      result.push(n)
      if (n.childModules?.length) walk(n.childModules)
    }
  }
  walk(Array.isArray(nodes) ? nodes : [nodes])
  return result
}

/** 递归展平子模块列表，保留_depth用于缩进 */
function flattenModuleChildren(children, depth) {
  if (!children) return []
  const result = []
  for (const c of children) {
    c._depth = depth
    result.push(c)
    if (c.childModules?.length) {
      result.push(...flattenModuleChildren(c.childModules, depth + 1))
    }
  }
  return result
}

/** 扁平化组织/角色树（用于名称查找） */
function flattenOrgRoleTreeFlat(nodes) {
  if (!nodes) return []
  const result = []
  const walk = (items) => {
    for (const n of items) {
      const children = n.children || n.childFolder
      result.push({ id: n.guid, guid: n.guid, name: n.ousName || n.oUsName || n.OUsName || n.name || '' })
      if (children?.length) walk(children)
    }
  }
  walk(Array.isArray(nodes) ? nodes : [nodes])
  return result
}

/* ---------- 数据加载 ---------- */

/** 递归映射模块节点添加_depth字段 */
function mapModuleNodes(nodes, depth) {
  if (!nodes || !nodes.length) return []
  return nodes.map(m => ({
    guid: m.guid,
    name: m.name || '',
    icon: m.icon || 'fa-cube',
    url: m.url || '',
    superGuid: m.superGuid || '',
    _depth: depth,
    childModules: mapModuleNodes(m.childModules, depth + 1)
  }))
}

/** 根据 superGuid 将平铺列表构建为树 */
function buildTreeFromFlat(flatList) {
  const map = {}
  const roots = []
  // 先建立 guid → node 映射
  for (const m of flatList) {
    map[m.guid] = { ...m, childModules: [] }
  }
  // 挂载子节点
  for (const m of flatList) {
    const node = map[m.guid]
    const parentGuid = m.superGuid || ''
    if (parentGuid && map[parentGuid]) {
      map[parentGuid].childModules.push(node)
    } else {
      roots.push(node)
    }
  }
  // 递归添加_depth
  function setDepth(nodes, depth) {
    for (const n of nodes) {
      n._depth = depth
      if (n.childModules?.length) setDepth(n.childModules, depth + 1)
    }
  }
  setDepth(roots, 0)
  return roots
}

/** 加载模块列表 */
async function loadModules() {
  try {
    const res = await getModules()
    const list = Array.isArray(res) ? res : (res?.data || [])
    // 后端返回平铺列表，前端根据 superGuid 构建树
    moduleTree.value = buildTreeFromFlat(list)
    flatModules.value = flattenTree(moduleTree.value)
  } catch (e) {
    console.error('加载模块列表失败', e)
  }
}

/** 加载指定模块及其子模块的权限 */
async function loadPermissionsForModule(mod) {
  const allGuids = [mod.guid]
  // 递归收集所有子孙模块 guid
  const collectGuids = (node) => {
    if (node.childModules?.length) {
      for (const c of node.childModules) {
        allGuids.push(c.guid)
        collectGuids(c)
      }
    }
  }
  collectGuids(mod)

  const allPerms = []
  let idSeq = 1
  for (const g of allGuids) {
    try {
      const res = await getModulePermissions(g)
      const items = Array.isArray(res) ? res : (res?.data || [])
      // 找到对应模块名称
      const modInfo = flatModules.value.find(m => m.guid === g)
      const modName = modInfo?.name || g
      for (const item of items) {
        const opType = (item.operatorType || '').toLowerCase()
        const opID = item.operatorID || ''
        let targetName = item.operatorPara1 || opID
        // 查找授权对象显示名称
        if (opType === 'user') {
          const u = users.value.find(u => u.id === opID || u.guid === opID)
          if (u) targetName = u.name
        } else if (opType === 'org') {
          const o = orgs.value.find(o => o.id === opID || o.guid === opID)
          if (o) targetName = o.name
        } else if (opType === 'role') {
          const r = rls.value.find(r => r.id === opID || r.guid === opID)
          if (r) targetName = r.name
        }
        allPerms.push({
          id: idSeq++,
          guid: item.guid,
          moduleId: g,
          moduleName: modName,
          targetType: opType,
          targetId: opID,
          targetName: targetName,
          scope: (item.alloyPermission && item.alloyPermission !== '0') ? 'manage' : 'view',
          status: item.active === 1 ? '启用' : '禁用',
          remark: item.operatorPara1 || ''
        })
      }
    } catch (e) {
      console.error(`加载模块 [${g}] 权限失败`, e)
    }
  }
  permissions.value = allPerms
}

/** 加载用户列表 */
async function loadUsers() {
  try {
    const res = await getUserList({ pageSize: 9999 })
    const list = Array.isArray(res) ? res : (res?.data || [])
    users.value = list.map(u => ({ id: u.guid, guid: u.guid, name: u.displayName || u.account || '' }))
  } catch (e) {
    console.error('加载用户列表失败', e)
  }
}

/** 加载组织列表 */
async function loadOrgs() {
  try {
    const res = await getOrgList()
    const list = Array.isArray(res) ? res : (res?.data || [])
    orgTree.value = list
    orgs.value = flattenOrgRoleTreeFlat(list)
  } catch (e) {
    try {
      const res2 = await getOrgTree()
      const list2 = Array.isArray(res2) ? res2 : (res2?.data || [])
      orgTree.value = list2
      orgs.value = flattenOrgRoleTreeFlat(list2)
    } catch (e2) {
      console.error('加载组织列表失败', e2)
    }
  }
}

/** 加载角色列表 */
async function loadRoles() {
  try {
    const res = await getRoleList()
    const list = Array.isArray(res) ? res : (res?.data || [])
    roleTree.value = list
    rls.value = flattenOrgRoleTreeFlat(list)
  } catch (e) {
    console.error('加载角色列表失败', e)
  }
}

onMounted(() => {
  loadModules()
  loadUsers()
  loadOrgs()
  loadRoles()
})

/* ---------- 计算属性 ---------- */

const filteredData = computed(() => {
  let r = permissions.value
  if (searchForm.moduleName) r = r.filter(p => p.moduleName.includes(searchForm.moduleName))
  if (searchForm.targetType) r = r.filter(p => p.targetType === searchForm.targetType)
  return r
})
const totalCount = computed(() => filteredData.value.length)
const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value) || 1)
const paginatedData = computed(() => {
  const s = (currentPage.value - 1) * pageSize.value
  return filteredData.value.slice(s, s + pageSize.value)
})
const visiblePages = computed(() => {
  const ps = [], t = totalPages.value, c = currentPage.value
  if (t <= 7) {
    for (let i = 1; i <= t; i++) ps.push(i)
  } else {
    ps.push(1)
    if (c > 4) ps.push('...')
    for (let i = Math.max(2, c - 2); i <= Math.min(t - 1, c + 2); i++) ps.push(i)
    if (c < t - 3) ps.push('...')
    ps.push(t)
  }
  return ps
})
const filteredModules = computed(() => {
  if (!moduleSearch.value) return flatModules.value
  return flatModules.value.filter(m => m.name.includes(moduleSearch.value))
})
const targetTypeLabel = computed(() => ({ user: '用户', org: '组织', role: '角色' })[form.targetType] || '')
function flattenForPicker(nodes, level = 0) {
  let result = []
  for (const n of (nodes || [])) {
    const name = form.targetType === 'org'
      ? (n.ousName || n.oUsName || n.OUsName || '')
      : (n.roleName || n.name || '')
    const children = n.children || n.childFolder || []
    result.push({ id: n.guid, name, level, hasChildren: children.length > 0 })
    if (children.length > 0) {
      result = result.concat(flattenForPicker(children, level + 1))
    }
  }
  return result
}
const filteredTargets = computed(() => {
  if (form.targetType === 'user') {
    return !targetSearch.value ? users.value : users.value.filter(i => i.name.includes(targetSearch.value))
  }
  const tree = form.targetType === 'org' ? orgTree.value : roleTree.value
  let flat = flattenForPicker(tree)
  if (targetSearch.value) {
    flat = flat.filter(i => (i.name || '').includes(targetSearch.value))
  }
  return flat
})

/* ---------- 模块树操作 ---------- */

const toggleExpandModule = (guid) => {
  const idx = expandedModuleGuids.value.indexOf(guid)
  if (idx !== -1) expandedModuleGuids.value.splice(idx, 1)
  else expandedModuleGuids.value.push(guid)
}

const selectModule = (node) => {
  selectedModuleGuid.value = node.guid
  currentPage.value = 1
  loadPermissionsForModule(node)
}

/* ---------- 搜索/分页 ---------- */

const handleSearch = () => { currentPage.value = 1 }
const resetSearch = () => {
  searchForm.moduleName = ''
  searchForm.targetType = ''
  currentPage.value = 1
}
const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }
const goToPage = (p) => { if (typeof p === 'number' && p >= 1 && p <= totalPages.value) currentPage.value = p }

/* ---------- 表单操作 ---------- */

const openAddDialog = () => {
  editingId.value = null
  // 默认选中当前模块
  const currentMod = flatModules.value.find(m => m.guid === selectedModuleGuid.value)
  Object.assign(form, {
    moduleId: currentMod?.guid || null,
    moduleName: currentMod?.name || '',
    targetType: 'user',
    targetId: null,
    targetName: '',
    scope: 'view',
    status: '启用',
    remark: ''
  })
  showFormModal.value = true
}

const editPermission = (p) => {
  editingId.value = p.id
  Object.assign(form, {
    moduleId: p.moduleId,
    moduleName: p.moduleName,
    targetType: p.targetType,
    targetId: p.targetId,
    targetName: p.targetName,
    scope: p.scope,
    status: p.status,
    remark: p.remark || ''
  })
  showFormModal.value = true
}

const closeFormModal = () => {
  showFormModal.value = false
  editingId.value = null
}

const saveForm = async () => {
  if (!form.moduleId || !form.targetId) {
    $message.warning('请选择模块和授权对象')
    return
  }
  const payload = {
    Guid: editingId.value ? (permissions.value.find(p => p.id === editingId.value)?.guid || undefined) : undefined,
    OperatorType: targetTypeToOperator[form.targetType] || form.targetType,
    OperatorID: form.targetId,
    OperatorPara1: form.targetName,
    ResourceType: 'Module',
    ResourceID: form.moduleId,
    AlloyPermission: form.scope === 'manage' ? '1' : '0',
    Active: form.status === '启用' ? 1 : 0
  }
  try {
    await saveModulePermission(payload)
    closeFormModal()
    // 重新加载当前选中模块的权限
    const mod = flatModules.value.find(m => m.guid === selectedModuleGuid.value)
    if (mod) await loadPermissionsForModule(mod)
  } catch (e) {
    console.error('保存权限失败', e)
    $message.error('保存失败，请重试')
  }
}

const toggleStatus = async (p) => {
  const newStatus = p.status === '启用' ? '禁用' : '启用'
  try {
    await saveModulePermission({
      Guid: p.guid,
      OperatorType: targetTypeToOperator[p.targetType] || p.targetType,
      OperatorID: p.targetId,
      OperatorPara1: p.targetName,
      ResourceType: 'Module',
      ResourceID: p.moduleId,
      AlloyPermission: p.scope === 'manage' ? '1' : '0',
      Active: newStatus === '启用' ? 1 : 0
    })
    p.status = newStatus
    $message.success('已' + newStatus)
  } catch (e) {
    console.error('切换状态失败', e)
    $message.error(newStatus + '失败，请重试')
  }
}

const deletePermission = async (p) => {
  if (!await $confirm('确定删除该权限记录吗？')) return
  try {
    await deleteModulePermission(p.guid)
    permissions.value = permissions.value.filter(x => x.id !== p.id)
  } catch (e) {
    console.error('删除权限失败', e)
    $message.error('删除失败，请重试')
  }
}

/* ---------- 选择器 ---------- */

const selectModuleInPicker = (m) => {
  form.moduleId = m.guid
  form.moduleName = m.name
  showModulePicker.value = false
}

const selectTarget = (item) => {
  form.targetId = item.id
  form.targetName = item.name
  showTargetPicker.value = false
}

const openTargetPicker = () => {
  targetSearch.value = ''
  showTargetPicker.value = true
}
</script>

<style scoped>
.view-container { padding: 16px; background: #f5f7fa; }
.main-layout { display: flex; gap: 16px; min-height: calc(94vh - 110px); max-height: calc(94vh - 110px); }
.left-panel { flex: 0 0 22%; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; overflow: hidden; display: flex; flex-direction: column; }
.panel-header { padding: 10px 12px; border-bottom: 1px solid #e8ecf1; background: #fafbfc; }
.panel-header h4 { margin: 0; font-size: 13px; font-weight: 600; color: #374151; }
.panel-body { height: 100%; padding: 8px; overflow-y: auto; }
.right-panel { flex: 1; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; overflow: hidden; }
.tree-table { width: 100%; border-collapse: collapse; }
.tree-table thead th { padding: 8px 10px; font-size: 11px; font-weight: 600; color: #6b7280; background: #f8fafc; border-bottom: 1px solid #e8ecf1; text-align: left; }
.tree-table tbody td { padding: 7px 10px; border-bottom: 1px solid #f3f4f6; font-size: 12px; color: #374151; }
.tree-row { cursor: pointer; transition: background 0.1s; }
.tree-row:hover { background: #f9fafb; }
.tree-row.selected { background: #ecfdf5; }
.tree-row.selected td { color: #059669; }
.tree-name-cell { display: flex; align-items: center; gap: 4px; }
.expand-icon { width: 14px; height: 14px; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 9px; cursor: pointer; }
.expand-icon:hover { color: #10b981; }
.expand-icon.expanded i { transform: rotate(90deg); }
.expand-placeholder { width: 14px; }
.tree-name-cell i { color: #10b981; font-size: 11px; }
.tree-name { margin-left: 2px; font-size: 12px; }

.search-panel { padding: 10px 14px; background: #fafbfc; border-bottom: 1px solid #e8ecf1; }
.search-row { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
.search-item { display: flex; align-items: center; gap: 6px; }
.search-item label { font-size: 13px; color: #374151; font-weight: 500; white-space: nowrap; }
.search-item label i { color: #10b981; margin-right: 2px; font-size: 12px; }
.search-input, .search-select { padding: 7px 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; width: 160px; outline: none; transition: border-color 0.2s; }
.search-input:focus, .search-select:focus { border-color: #10b981; }
.search-actions { display: flex; gap: 6px; margin-left: auto; align-items: center; }
.search-btn { padding: 6px 12px; font-size: 12px; border-radius: 6px; display: flex; align-items: center; gap: 4px; }

.table-container-inner { flex: 1; overflow-y: auto; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th, .data-table td { padding: 8px 10px; border-bottom: 1px solid #e7eaec; font-size: 12px; color: #676a6c; text-align: left; white-space: nowrap; }
.data-table th { background: #f5f5f5; font-weight: 600; position: sticky; top: 0; z-index: 2; }
.data-table tbody tr:hover { background: #f9fafb; }
.empty-cell { text-align: center; padding: 40px; color: #999; }
.action-cell { white-space: nowrap; text-align: center; }
.action-cell .btn-xs { margin: 0 2px; padding: 4px 8px; font-size: 11px; border-radius: 4px; cursor: pointer; border: 1px solid transparent; }
.btn-primary.btn-xs { background: #3498db; color: #fff; }
.btn-warning.btn-xs { background: #f39c12; color: #fff; }
.btn-danger.btn-xs { background: #e74c3c; color: #fff; }
.btn-success.btn-xs { background: #27ae60; color: #fff; }
.type-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 500; background: #f0f4ff; color: #6366f1; }
.status-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 500; }
.status-badge.enabled { background: #dcfce7; color: #16a34a; }
.status-badge.disabled { background: #fee2e2; color: #dc2626; }

.pagination-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #f9f9f9; border-top: 1px solid #e7eaec; flex-shrink: 0; }
.pagination-info { font-size: 12px; color: #676a6c; }
.pagination-controls { display: flex; align-items: center; gap: 8px; }
.page-numbers { display: flex; gap: 4px; }
.page-btn { width: 28px; height: 28px; border: 1px solid #e7eaec; background: #fff; color: #676a6c; border-radius: 3px; cursor: pointer; font-size: 12px; display: flex; align-items: center; justify-content: center; }
.page-btn:hover:not(.disabled) { border-color: #10b981; color: #10b981; }
.page-btn.active { background: #10b981; border-color: #10b981; color: #fff; }
.page-btn.disabled { cursor: not-allowed; opacity: 0.5; }
.pagination-btn { width: 28px; height: 28px; padding: 0; display: flex; align-items: center; justify-content: center; }
.page-size-selector { display: flex; align-items: center; gap: 4px; font-size: 12px; }
.page-size-input { width: 60px; padding: 3px 6px; font-size: 12px; }

.empty-state { text-align: center; padding: 60px 20px; color: #9ca3af; }
.empty-state i { font-size: 48px; margin-bottom: 12px; color: #d1d5db; }
.empty-state p { font-size: 14px; margin-bottom: 4px; }
.empty-state .hint { font-size: 12px; color: #d1d5db; }

.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-container { background: #fff; border-radius: 8px; width: 600px; max-height: 80vh; overflow-y: auto; }
.picker-modal { width: 450px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-bottom: 1px solid #e7eaec; }
.modal-header h3 { margin: 0; font-size: 16px; }
.close-btn { background: none; border: none; font-size: 22px; cursor: pointer; color: #999; }
.close-btn:hover { color: #333; }
.modal-body { padding: 20px; }
.modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; }
.form-row { display: flex; gap: 15px; margin-bottom: 15px; }
.form-group { flex: 1; }
.form-group label { display: block; font-size: 13px; color: #676a6c; margin-bottom: 5px; font-weight: 500; }
.form-group .form-control { width: 100%; padding: 8px 12px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 13px; box-sizing: border-box; }
.form-group textarea.form-control { resize: vertical; }
.search-select { display: flex; gap: 6px; }
.search-select .form-control { flex: 1; cursor: pointer; }
.search-btn { padding: 6px 10px; border: 1px solid #e7eaec; border-radius: 4px; background: #f9f9f9; cursor: pointer; color: #676a6c; }
.search-btn:hover { background: #e7eaec; }
.search-box { margin-bottom: 12px; }
.search-input-wrap { position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 10px; color: #9ca3af; font-size: 13px; z-index: 1; }
.search-box-input { width: 100%; padding: 8px 10px 8px 30px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; outline: none; box-sizing: border-box; transition: border-color 0.2s; }
.search-box-input:focus { border-color: #10b981; }
.picker-list { max-height: 320px; overflow-y: auto; }
.picker-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-bottom: 1px solid #f3f4f6; cursor: pointer; font-size: 13px; color: #374151; transition: background 0.15s; }
.picker-item:hover { background: #f9fafb; }
.picker-item.selected { background: #ecfdf5; color: #059669; font-weight: 500; }
.picker-item i { color: #10b981; font-size: 12px; width: 16px; text-align: center; flex-shrink: 0; }
.tree-level-0 { padding-left: 14px; }
.tree-level-1 { padding-left: 36px; }
.tree-level-2 { padding-left: 58px; }
.tree-level-3 { padding-left: 80px; }
.tree-hint { margin-left: auto; font-size: 10px; color: #d1d5db; }
.picker-empty { text-align: center; padding: 30px 0; color: #9ca3af; font-size: 13px; }

.btn { padding: 6px 14px; border: 1px solid transparent; border-radius: 4px; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.2s; }
.btn-primary { background: #10b981; color: #fff; border-color: #10b981; }
.btn-primary:hover { background: #059669; }
.btn-default { background: #fff; color: #374151; border-color: #d1d5db; }
.btn-default:hover { background: #f9fafb; }
.btn-success { background: #6366f1; color: #fff; border-color: #6366f1; }
.btn-success:hover { background: #4f46e5; }
.btn-warning { background: #f59e0b; color: #fff; border-color: #f59e0b; }
.btn-warning:hover { background: #d97706; }
.btn-sm { padding: 5px 10px; font-size: 12px; }
</style>
